import { useRef, useState } from 'react';
import CheckoutAlert from '../CheckoutAlert';
import AddressFields from '../AddressFields';
import {
  CheckRow,
  ChoiceGroup,
  SectionHeading,
  TextField,
  buttonClass,
} from '../CheckoutControls';
import { verifyAddress } from '@/lib/checkout/services';
import { isUsStateCode, validateContactStep } from '@/lib/checkout/validation';
import type { AddressDraft, ContactDraft, FieldErrors } from '@/lib/checkout/types';

interface ContactInformationStepProps {
  value: ContactDraft;
  onChange: (patch: Partial<ContactDraft>) => void;
  onNext: () => void;
  onBack: () => void;
}

type AddressTarget = 'billing' | 'mailing';

interface VerificationPrompt {
  target: AddressTarget;
  kind: 'suggestion' | 'no_match' | 'unavailable';
  suggested?: AddressDraft;
}

const formatAddress = (address: AddressDraft): string =>
  [
    [address.street, address.unit].filter(Boolean).join(', '),
    `${address.city}, ${address.state} ${address.zip}`,
  ].join('\n');

const TARGET_LABEL: Record<AddressTarget, string> = {
  billing: 'billing address',
  mailing: 'mailing address',
};

/** Step 2 — Contact Information, incl. USPS verification of both addresses. */
export default function ContactInformationStep({
  value,
  onChange,
  onNext,
  onBack,
}: ContactInformationStepProps) {
  const [submitted, setSubmitted] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [prompt, setPrompt] = useState<VerificationPrompt | null>(null);
  const [resolved, setResolved] = useState<Record<AddressTarget, boolean>>({
    billing: false,
    mailing: false,
  });
  const summaryRef = useRef<HTMLDivElement>(null);

  // Errors are derived once they've tried to continue, so they clear as fields are fixed.
  const errors: FieldErrors = submitted ? validateContactStep(value) : {};
  const errorList = Object.keys(errors);

  // A postal code with letters (e.g. "M5S 1A1") means they're entering a
  // non-US address — the state list only offers US states and territories.
  const billingOutsideUs =
    /[a-z]/i.test(value.billing.zip) ||
    (Boolean(value.billing.state) && !isUsStateCode(value.billing.state));

  const patchAddress = (target: AddressTarget, patch: Partial<AddressDraft>) => {
    onChange({ [target]: { ...value[target], ...patch } } as Partial<ContactDraft>);
    setResolved((current) => ({ ...current, [target]: false }));
    setPrompt(null);
  };

  /** Verifies each address that still needs it; shows a prompt or advances. */
  const verifyAndContinue = async (overrides?: Partial<Record<AddressTarget, boolean>>) => {
    const done = { ...resolved, ...overrides };
    const targets: AddressTarget[] = value.mailingSameAsBilling
      ? ['billing']
      : ['billing', 'mailing'];

    setVerifying(true);
    try {
      for (const target of targets) {
        if (done[target]) continue;

        let result: Awaited<ReturnType<typeof verifyAddress>>;
        try {
          result = await verifyAddress(value[target]);
        } catch {
          // Treat a failed call like an outage so they can continue as entered.
          result = { status: 'unavailable' };
        }

        if (result.status === 'verified') {
          done[target] = true;
          setResolved((current) => ({ ...current, [target]: true }));
          continue;
        }

        setPrompt({
          target,
          kind: result.status === 'suggestion' ? 'suggestion' : result.status,
          suggested: result.status === 'suggestion' ? result.suggested : undefined,
        });
        return;
      }

      setPrompt(null);
      onNext();
    } finally {
      setVerifying(false);
    }
  };

  const submit = async () => {
    setSubmitted(true);

    if (Object.keys(validateContactStep(value)).length > 0) {
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }

    await verifyAndContinue();
  };

  const useSuggested = async () => {
    if (!prompt?.suggested) return;
    const target = prompt.target;
    onChange({ [target]: prompt.suggested } as Partial<ContactDraft>);
    setResolved((current) => ({ ...current, [target]: true }));
    setPrompt(null);
    await verifyAndContinue({ [target]: true });
  };

  const useAsEntered = async () => {
    if (!prompt) return;
    const target = prompt.target;
    setResolved((current) => ({ ...current, [target]: true }));
    setPrompt(null);
    await verifyAndContinue({ [target]: true });
  };

  const editAddress = () => {
    const target = prompt?.target ?? 'billing';
    setPrompt(null);
    document.getElementById(`${target}-street`)?.focus();
  };

  const verificationPanel = prompt ? (
    <div
      role="group"
      aria-label={`Verify your ${TARGET_LABEL[prompt.target]}`}
      className="mb-4 rounded-2xl border border-[#f3d3c2] bg-[#fffaf6] p-4"
    >
      <h4 className="m-0 mb-2.5 text-[12.5px] font-bold text-[#b45309]">
        {prompt.kind === 'suggestion'
          ? `We couldn’t verify this ${TARGET_LABEL[prompt.target]}`
          : prompt.kind === 'no_match'
            ? `USPS couldn’t find this ${TARGET_LABEL[prompt.target]}`
            : 'Address verification is temporarily unavailable'}
      </h4>

      {prompt.kind === 'unavailable' ? (
        <p className="mb-3 text-[12.5px] leading-relaxed text-[#7c4a1d]">
          We can’t reach USPS right now. You can continue with the address as entered — we’ll verify
          it later and contact you if anything needs correcting.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2.5">
          <div className="min-w-[180px] flex-1 rounded-xl border border-[#ece6f0] bg-white p-3">
            <p className="mb-1 text-[8.5px] font-semibold uppercase tracking-[0.08em] text-[#7a6a8c]">
              You entered
            </p>
            <p className="whitespace-pre-line text-[12.5px] leading-relaxed text-[#3d1751]">
              {formatAddress(value[prompt.target])}
            </p>
          </div>
          {prompt.suggested ? (
            <div className="min-w-[180px] flex-1 rounded-xl border border-[#b6f0e2] bg-white p-3">
              <p className="mb-1 text-[8.5px] font-semibold uppercase tracking-[0.08em] text-[#0f7a63]">
                USPS suggests
              </p>
              <p className="whitespace-pre-line text-[12.5px] leading-relaxed text-[#3d1751]">
                {formatAddress(prompt.suggested)}
              </p>
            </div>
          ) : (
            <div className="min-w-[180px] flex-1 rounded-xl border border-[#ece6f0] bg-white p-3">
              <p className="text-[12.5px] leading-relaxed text-[#7c4a1d]">
                USPS has no match for this address. Correct it, or continue with it as entered.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {prompt.suggested ? (
          <button type="button" onClick={useSuggested} className={buttonClass('sm')}>
            Use suggested address
          </button>
        ) : null}
        <button type="button" onClick={editAddress} className={buttonClass('smOutline')}>
          Edit address
        </button>
        <button type="button" onClick={useAsEntered} className={buttonClass('smGhost')}>
          Use as entered
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <h2 className="m-0 mb-5 text-xl font-bold text-[#4b0a5b] sm:text-[21px]">
        Contact Information
      </h2>

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <TextField
          id="contact-legal-name"
          label="Full Legal Name"
          required
          autoComplete="name"
          value={value.legalName}
          error={errors.legalName}
          onChange={(event) => onChange({ legalName: event.target.value })}
        />

        <SectionHeading>Billing address</SectionHeading>
        <AddressFields
          prefix="billing"
          value={value.billing}
          errors={errors}
          onChange={(patch) => patchAddress('billing', patch)}
        />

        {billingOutsideUs ? (
          <CheckoutAlert
            tone="error"
            title="Billing address must be in the US or a US territory."
            compact
          >
            Memberships are billed in USD to a US address. Puerto Rico, Guam, US Virgin Islands,
            American Samoa and Northern Mariana Islands are accepted.
          </CheckoutAlert>
        ) : null}

        {prompt?.target === 'billing' ? verificationPanel : null}

        <CheckRow
          id="contact-mailing-same"
          checked={value.mailingSameAsBilling}
          onChange={(checked) => {
            onChange({ mailingSameAsBilling: checked });
            setPrompt(null);
          }}
        >
          Mailing address same as billing
        </CheckRow>

        {!value.mailingSameAsBilling ? (
          <>
            <SectionHeading>Mailing address</SectionHeading>
            <AddressFields
              prefix="mailing"
              value={value.mailing}
              errors={errors}
              onChange={(patch) => patchAddress('mailing', patch)}
            />
            {prompt?.target === 'mailing' ? verificationPanel : null}
          </>
        ) : null}

        <TextField
          id="contact-phone"
          label="Phone"
          type="tel"
          required
          autoComplete="tel"
          value={value.phone}
          error={errors.phone}
          onChange={(event) => onChange({ phone: event.target.value })}
        />

        <TextField
          id="contact-agency-name"
          label="Your Travel Agency Name"
          required
          hint="You can always change this later"
          value={value.agencyName}
          error={errors.agencyName}
          onChange={(event) => onChange({ agencyName: event.target.value })}
        />

        <ChoiceGroup
          legend="Do you have associate advisors?"
          name="contact-has-associates"
          required
          note="Team and Enterprise plans only"
          value={value.hasAssociates}
          error={errors.hasAssociates}
          options={[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
          onChange={(next) =>
            onChange({
              hasAssociates: next as ContactDraft['hasAssociates'],
              associateCount: next === 'no' ? '' : value.associateCount,
            })
          }
          help={
            <a href="/landing#associate-advisors" className="font-medium text-[#b82f74] underline">
              Learn more about associate advisors.
            </a>
          }
        />

        {value.hasAssociates === 'yes' ? (
          <TextField
            id="contact-associate-count"
            label="How many associate advisors do you have?"
            required
            inputMode="numeric"
            className="max-w-[200px]"
            value={value.associateCount}
            error={errors.associateCount}
            onChange={(event) => onChange({ associateCount: event.target.value })}
          />
        ) : null}

        <TextField
          id="contact-referred-by"
          label="Referred by a current member? Enter their full name"
          optional
          value={value.referredBy}
          onChange={(event) => onChange({ referredBy: event.target.value })}
        />

        {errorList.length > 0 ? (
          <div ref={summaryRef} tabIndex={-1}>
            <CheckoutAlert tone="error" title="Check the highlighted fields." compact>
              {errorList.length === 1
                ? '1 field needs attention before you can continue.'
                : `${errorList.length} fields need attention before you can continue.`}
            </CheckoutAlert>
          </div>
        ) : null}

        <div className="mt-4 flex gap-2">
          <button type="button" onClick={onBack} className={buttonClass('outline')}>
            Back
          </button>
          <button type="submit" disabled={verifying} className={buttonClass('primary')}>
            {verifying ? 'Verifying address…' : 'Next'}
          </button>
        </div>
      </form>
    </>
  );
}
